<template>
  <!--
    toast "เพิ่งอัปเดต" ต้องอยู่เหนือ ModalEmployeePin (z-10) เพราะพอ reload เสร็จ
    หน้าจอจะกลับไปล็อคเสมอ — ถ้าใช้ SharedToast (z ต่ำกว่า) จะโดนฉากล็อคบัง
  -->
  <div
    v-if="updatedToastVisible"
    class="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center py-4"
  >
    <div class="rounded bg-green-200 px-4 py-2 text-center text-green-900 shadow-lg">
      <div class="text-lg">{{ $t("app_reload.label.updated") }}</div>
      <div v-if="store.updatedMessage" class="whitespace-pre-line text-base">
        {{ store.updatedMessage }}
      </div>
    </div>
  </div>

  <!--
    reload รอนานผิดปกติเพราะมีคิวส่งข้อมูลค้าง — ให้คนกดเอง (คิวอยู่ใน localStorage ส่งต่อหลังโหลด)
    ไม่โชว์ตอนออฟไลน์ (โหลดตอนนี้ = จอขาว) / ตะกร้ามีของ / กำลังคิดเงิน (คนกำลังใช้อยู่ เดี๋ยวก็ว่าง)
  -->
  <div
    v-if="stuckBannerVisible"
    class="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-4 py-3"
  >
    <div class="flex items-center gap-3 rounded bg-yellow-100 px-4 py-2 text-yellow-900 shadow-lg">
      <span>{{ $t("app_reload.label.stuck") }}</span>
      <button class="rounded bg-yellow-600 px-3 py-1 text-white" @click="reloadFromBanner">
        {{ $t("app_reload.btn.reload_now") }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * โหลดหน้าใหม่ตามคำสั่งจาก Firestore (ตัดสินที่ plugins/appReload.plugin.ts) — ไม่ถาม ไม่มี modal
 *
 * - เครื่องว่าง → โหลดเลย ตามเวลาที่สุ่มไว้ 0-60 วิ (pendingReload.reloadAt) กันทุกเครื่องยิงพร้อมกัน
 * - เครื่องมีงานค้าง (หน้าเพิ่มสินค้า / ตะกร้ามีของ / คิดเงิน / คิว sync ค้าง / ออฟไลน์ — ดู useAppBusy.ts)
 *   → ยังไม่โหลด รอจนกลับมาว่าง แล้วโหลด (ถ้าเลยเวลาที่สุ่มไว้แล้วโหลดทันที)
 * - ไม่ต้องล็อค PIN — ล็อคมีผลแค่ "อยู่หน้าเพิ่มสินค้าแต่ตะกร้าว่าง" ถือว่าว่าง
 *
 * หลังโหลดเสร็จขึ้น toast "ระบบมีการอัปเดตเรียบร้อยแล้ว" + reload.message เป็นบรรทัดที่สอง (ถ้ามี)
 *
 * mount อยู่ใน layout หลักตัวเดียว หน้า login / expired จึงไม่โหลดระหว่างค้าง
 * พอกลับเข้าหน้าขายเมื่อไหร่ watcher ก็ทำงานต่อทันที
 */
import * as Sentry from "@sentry/vue";

const UPDATED_TOAST_MS = 30_000;
/** รอเครื่องว่างนานเกินนี้ = ติด (ปกติงานค้างจบในไม่กี่นาที) → แจ้ง Sentry + แบนเนอร์ */
const STUCK_MS = 60 * 60 * 1000;

const store = useAppControlStore();
const { isBusy, isOffline, hasQueuedSync, hasUnsavedCart, isOnPayment, isOnCartPage, isLocked } = useAppBusy();

let reloadTimer: ReturnType<typeof setTimeout> | null = null;
// กันยิงซ้ำ: location.replace ไม่ได้หยุดโค้ดทันที watcher อาจถูกประเมินอีกรอบ
// ระหว่างที่เบราว์เซอร์กำลังเริ่มโหลด แล้วสั่ง replace ซ้อนกันจน history เพี้ยน
let reloading = false;

function clearReloadTimer() {
  if (reloadTimer !== null) {
    clearTimeout(reloadTimer);
    reloadTimer = null;
  }
}

watchEffect(() => {
  const pending = store.pendingReload;
  clearReloadTimer();
  if (!pending || isBusy.value || reloading) return;

  // ตั้ง timer ใหม่ทุกครั้งที่สถานะเปลี่ยน — ระหว่างรอถ้าเครื่องกลับไปมีงานค้าง
  // watchEffect รันใหม่แล้วยกเลิก timer เอง ไม่โหลดทับงาน
  reloadTimer = setTimeout(() => {
    reloadTimer = null;
    if (reloading || isBusy.value) return;
    reloading = true;
    store.reloadNow();
  }, Math.max(0, pending.reloadAt - Date.now()));
});

// ---- reload ค้างเพราะเครื่องไม่ว่างนานเกินไป ---------------------------------

const nowTs = ref(Date.now());
const nowTimer = setInterval(() => (nowTs.value = Date.now()), 60_000);
let reportedStuckHash: string | null = null;

const stuck = computed(() => {
  const pending = store.pendingReload;
  return !!pending && isBusy.value && !reloading && nowTs.value - pending.createdAt > STUCK_MS;
});

const stuckBannerVisible = computed(
  () => stuck.value && hasQueuedSync.value && !isOffline.value && !hasUnsavedCart.value && !isOnPayment.value
);

// ส่งครั้งเดียวต่อคำสั่ง พร้อมเหตุที่ติด ไว้ไล่ว่าเครื่องไหนไม่ได้ bundle ใหม่เพราะอะไร
watch(stuck, (value) => {
  const pending = store.pendingReload;
  if (!value || !pending || reportedStuckHash === pending.hash) return;
  reportedStuckHash = pending.hash;
  try {
    Sentry.captureMessage("[appReload] รอเครื่องว่างนานเกินไป", {
      level: "warning",
      extra: {
        source: pending.source,
        hash: pending.hash,
        reason: pending.reason,
        waitingMinutes: Math.round((Date.now() - pending.createdAt) / 60_000),
        isOffline: isOffline.value,
        hasQueuedSync: hasQueuedSync.value,
        hasUnsavedCart: hasUnsavedCart.value,
        isOnPayment: isOnPayment.value,
        isOnCartPage: isOnCartPage.value,
        isLocked: isLocked.value,
      },
    });
  } catch {}
});

function reloadFromBanner() {
  if (reloading) return;
  reloading = true;
  store.reloadNow();
}

// ---- toast "เพิ่งอัปเดต" หลังโหลดเสร็จ ------------------------------------

const updatedToastVisible = ref(false);
let updatedToastTimer: ReturnType<typeof setTimeout> | null = null;

/** เก็บเฉพาะของที่เป็นของคอมโพเนนต์ — deadline อยู่ในสโตร์ จึงรอด remount */
function detachUpdatedToast() {
  updatedToastVisible.value = false;

  if (updatedToastTimer !== null) {
    clearTimeout(updatedToastTimer);
    updatedToastTimer = null;
  }

  window.removeEventListener("click", dismissUpdatedToast, true);
  window.removeEventListener("keydown", dismissUpdatedToast, true);
}

function dismissUpdatedToast() {
  store.hideUpdatedToast();
  detachUpdatedToast();
}

/** ผูกการแสดงผลกับ deadline ในสโตร์ — เรียกซ้ำได้ทุกครั้งที่ mount ใหม่ */
function syncUpdatedToast() {
  detachUpdatedToast();

  if (store.updatedToastUntil === null) return;

  const remaining = store.updatedToastUntil - Date.now();
  if (remaining <= 0) {
    store.hideUpdatedToast();
    return;
  }

  updatedToastVisible.value = true;
  updatedToastTimer = setTimeout(dismissUpdatedToast, remaining);

  // ปัดทิ้งเมื่อเริ่มใช้งาน (คลิกที่ไหนก็ได้ / กดแป้นตอนใส่ PIN)
  // capture phase เพื่อให้จับได้แม้คลิกโดนฉากล็อคหน้าจอ
  window.addEventListener("click", dismissUpdatedToast, true);
  window.addEventListener("keydown", dismissUpdatedToast, true);
}

// รอจน layout หลักขึ้นแล้วค่อยขึ้น toast — ถ้ายิงตอน plugin จะหมดอายุไปก่อน
// ผู้ใช้เห็น เพราะกว่าจะผ่าน splash / bootstrap ใช้เวลาพอสมควร
onMounted(() => {
  if (store.consumeJustUpdated()) store.startUpdatedToast(UPDATED_TOAST_MS);
  syncUpdatedToast();
});

onBeforeUnmount(() => {
  clearReloadTimer();
  clearInterval(nowTimer);
  // ถอดเฉพาะ timer/listener ไม่ล้าง deadline — mount รอบหน้าจะโชว์ต่อจนครบเวลา
  detachUpdatedToast();
});
</script>
